import React from 'react';
import { Container } from '@/components/layout/Container';
import { Section, SectionHeading } from '@/components/layout/Section';
import { Reveal } from '@/components/layout/Reveal';
import { CtaButton } from '@/components/site/CtaButton';
import { WhatsAppButton } from '@/components/site/WhatsAppButton';
import { PhotoFrame } from '@/components/site/PhotoFrame';
import { ProcessSteps } from '@/components/site/ProcessSteps';
import { CtaBlock } from '@/components/site/CtaBlock';
import { ServiceBlock } from '@/components/site/ServiceContent';
import { ServicesCinematicStage } from '@/components/site/ServicesCinematicStage';
import { PartnershipSection } from '@/components/site/PartnershipSection';
import { useCinematicStage } from '@/hooks/useCinematicStage';
import { CONSULTATION_PATH, IMAGES } from '@/lib/site';
import { useI18n } from '@/lib/i18n';

export default function Services() {
  const { t } = useI18n();
  const hero = t.servicesPage.hero;

  /**
   * The pinned reel runs on every ordinary desktop and laptop. It no longer
   * switches itself off on shorter screens — the stage scales its own frame to
   * fit instead. Phones, tablets and "reduce motion" get the stacked layout,
   * which carries its own, quieter parallax.
   */
  const { enabled: cinematic } = useCinematicStage();

  return (
    <>
      {/* -------------------------------- hero --------------------------------- */}
      <section
        data-testid="services-hero"
        className="relative overflow-hidden border-b border-[color:var(--rmh-border)]"
        style={{ backgroundColor: 'var(--rmh-canvas)' }}
      >
        <span
          aria-hidden="true"
          className="rmh-champagne-wash pointer-events-none absolute inset-0"
        />
        <Container wide className="relative z-10 py-14 lg:py-20">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="flex items-center gap-3">
                  <span
                    className="block h-px w-10"
                    style={{ backgroundColor: 'var(--rmh-accent)' }}
                    aria-hidden="true"
                  />
                  <span className="rmh-eyebrow">{hero.eyebrow}</span>
                </div>
                <h1
                  data-testid="services-hero-title"
                  className="rmh-display mt-7 text-[2.2rem] sm:text-[3rem] lg:text-[3.9rem]"
                >
                  {hero.titleLines.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </h1>
                <p className="rmh-body mt-7 max-w-xl text-[0.98rem] sm:text-[1.04rem]">
                  {hero.lede}
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <CtaButton
                    to={CONSULTATION_PATH}
                    size="lg"
                    variant="primary"
                    arrow
                    testId="services-hero-book-consultation-button"
                    className="w-full sm:w-auto"
                  >
                    {t.common.bookConsultation}
                  </CtaButton>
                  <WhatsAppButton
                    variant="outline"
                    size="lg"
                    long
                    testId="services-hero-whatsapp-link"
                    className="w-full sm:w-auto"
                  />
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.12} className="lg:col-span-5">
              <div className="mx-auto max-w-[400px] lg:max-w-none">
                <PhotoFrame
                  src={IMAGES.editorialPortrait}
                  webp={IMAGES.editorialPortraitWebp}
                  alt={t.photo.servicesHeroAlt}
                  ratio="4 / 5"
                  objectPosition="50% 48%"
                  caption={t.photo.servicesHeroCaption}
                  eager
                />
              </div>
            </Reveal>
          </div>

          {/* index nav */}
          <Reveal delay={0.2}>
            <nav
              aria-label={hero.indexNavLabel}
              data-testid="services-index-nav"
              className="mt-14 grid grid-cols-1 border-l border-t sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
              style={{ borderColor: 'var(--rmh-border)' }}
            >
              {t.services.map((s) => (
                <a
                  key={s.slug}
                  href={`#${s.slug}`}
                  data-testid={`services-index-${s.slug}-link`}
                  className="group flex items-baseline gap-3 border-b border-r px-5 py-5 transition-colors duration-300 hover:bg-[color:var(--rmh-surface)]"
                  style={{ borderColor: 'var(--rmh-border)' }}
                >
                  <span className="rmh-num text-[0.68rem] text-[color:var(--rmh-muted-2)]">
                    {s.number}
                  </span>
                  <span className="font-serif text-[0.98rem] leading-snug text-[color:var(--rmh-ink)] transition-transform duration-300 group-hover:translate-x-[2px]">
                    {s.title}
                  </span>
                </a>
              ))}
            </nav>
          </Reveal>
        </Container>
      </section>

      {/* ------------------------------ the blocks ------------------------------ */}
      {cinematic ? (
        <ServicesCinematicStage />
      ) : (
        <Section tone="canvas" wide className="pt-14 lg:pt-20">
          {t.services.map((service, i) => (
            <ServiceBlock key={service.slug} service={service} index={i} />
          ))}
        </Section>
      )}

      {/* ----------------------------- partnership ------------------------------ */}
      <PartnershipSection />

      {/* ------------------------------- process -------------------------------- */}
      <Section tone="canvas2" wide data-testid="services-process-section">
        <Reveal>
          <SectionHeading
            eyebrow={t.servicesPage.process.eyebrow}
            title={t.servicesPage.process.title}
            lede={t.servicesPage.process.lede}
          />
        </Reveal>
        <ProcessSteps />
      </Section>

      <CtaBlock
        eyebrow={t.servicesPage.finalCta.eyebrow}
        title={t.servicesPage.finalCta.title}
        body={t.servicesPage.finalCta.body}
        testIdPrefix="services-final-cta"
      />
    </>
  );
}
